import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'

type ReplaceOptions = {
  resetQueries?: string[]
}

export const useReplaceQuery = () => {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const getPath = (key: string, val: string, options?: ReplaceOptions) => {
    const query = []
    let isFresh = true
    for (const [k, v] of searchParams.entries()) {
      if (k === key) {
        if (val) {
          query.push(`${k}=${val}`)
        }
        isFresh = false
      } else {
        if (!options?.resetQueries?.includes(k)) {
          query.push(`${k}=${v}`)
        }
      }
    }

    if (isFresh) {
      query.push(`${key}=${val}`)
    }

    return `${pathname}?${query.join('&')}`
  }

  const replaceQuery = (key: string, val: string, options?: ReplaceOptions) => {
    router.replace(getPath(key, val, options), {
      scroll: false,
    })
  }

  const query: Record<string, string> = useMemo(() => {
    const query = {}
    for (const [k, v] of searchParams.entries()) {
      // @ts-ignore
      query[k] = v
    }
    return query
  }, [searchParams])

  const replaceQueries = useCallback(
    (record: Record<string, any>) => {
      const _query = { ...query }

      for (const [k, v] of Object.entries(record)) {
        if (v) {
          if (typeof v === 'object') {
            _query[k] = JSON.stringify(v)
          } else {
            _query[k] = v
          }
        } else {
          if (k in _query) {
            delete _query[k]
          }
        }
      }

      router.replace(
        `${pathname}?${Object.entries(_query)
          .map(([k, v]) => `${k}=${v}`)
          .join('&')}`,
        {
          scroll: false,
        },
      )
    },
    [query, pathname, router],
  )

  return {
    replaceQuery,
    replaceQueries,
    getPath,
    query,
  }
}
